import React, { useCallback, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    CircularProgress,
    Popper,
    PopperProps,
    Stack,
    styled,
    TextField,
} from '@mui/material';
import { useDebounce } from '@hooks/useDebounce';
import { useGetJobOffers } from '@modules/JobOffers/hooks/useGetJobOffers';
import Image from '@components/Image';

export interface JobOfferOption {
    _id: string;
    jobNumber: string;
    title: string;
    city: string;
    postalCode: string;
    pro?: {
        _id: string;
        commercialName: string;
        brandLogo?: {
            _id: string;
            url: string;
        };
    };
    brand?: {
        brandLogo?: {
            _id: string;
            url: string;
        };
    };
}

interface AutocompleteJobOfferProps {
    onSelect?: (jobOffer: JobOfferOption | null) => void;
    proId?: string | null;
    placeholder?: string;
    size?: 'small' | 'medium';
}

const EscHint = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    padding: '4px 8px',
    textAlign: 'right',
    color: theme.palette.text.secondary,
    fontSize: '12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
}));

const EscHintIcon = styled('i')(({ theme }) => ({
    marginRight: 4,
    fontSize: 12,
    color: theme.palette.text.secondary,
}));

const PopperWithEscHint = (props: PopperProps) => {
    const { children, ...other } = props;
    return (
        <Popper {...other}>
            <div>
                <EscHint>
                    <EscHintIcon className="fi fi-br-info" />
                    Appuyez sur Esc pour fermer la liste.
                </EscHint>
                {children as React.ReactNode}
            </div>
        </Popper>
    );
};

const LogoAdornment = styled('div')(() => ({
    width: 32,
    height: 32,
    // circle
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    '& img': {
        objectFit: 'contain',
        width: '100%',
        height: '100%',
    },
}));

/**
 * Job number regex pattern: 10 digits + 3 alphanumeric characters
 * Example: 2025020134AAA
 */
const JOB_NUMBER_REGEX = /^\d{10}[A-Z0-9]{3}$/;

/**
 * Detects if a search query is a job number or text search
 */
const isJobNumber = (query: string): boolean => {
    return JOB_NUMBER_REGEX.test(query.toUpperCase());
};

const AutocompleteJobOffer: React.FC<AutocompleteJobOfferProps> = ({
    onSelect,
    proId,
    placeholder = 'Rechercher une offre d\'emploi par numéro ou titre...',
    size = 'small',
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState<JobOfferOption | null>(null);

    const debouncedQuery = useDebounce(searchQuery, 500);

    // Determine filter based on query type
    const filter = useMemo(() => {
        const nextFilter: Record<string, unknown> = {};

        if (proId) {
            nextFilter.proId = [proId];
        }

        if (debouncedQuery && isJobNumber(debouncedQuery)) {
            nextFilter.jobNumber = debouncedQuery.toUpperCase();
        }

        return nextFilter;
    }, [debouncedQuery, proId]);

    // Use the unified hook for fetching job offers
    const { jobOffers, loading } = useGetJobOffers(
        !isJobNumber(debouncedQuery) ? debouncedQuery : '', // Pass text query only for non-job-number searches
        filter,
        'createdDate',
        0,
        15, // Limit results for autocomplete
    );

    // Transform job offers to autocomplete options
    const options: JobOfferOption[] = useMemo(() => {
        return jobOffers.map((offer: any) => ({
            _id: offer._id,
            jobNumber: offer.jobNumber,
            title: offer.title,
            city: offer.city,
            postalCode: offer.postalCode,
            pro: offer.pro
                ? {
                      _id: offer.pro._id,
                      commercialName: offer.pro.commercialName,
                  }
                : undefined,
            brand: offer.brand
                ? {
                      brandLogo: offer.brand.brandLogo,
                  }
                : undefined,
        }));
    }, [jobOffers]);

    const handleInputChange = useCallback(
        (_: React.SyntheticEvent, value: string, reason: 'input' | 'reset' | 'clear') => {
            if (reason === 'clear') {
                setSearchQuery('');
                setSelectedValue(null);
                onSelect?.(null);
            } else {
                setSearchQuery(value);
            }
        },
        [onSelect]
    );

    const handleChange = useCallback(
        (_: React.SyntheticEvent, value: JobOfferOption | null) => {
            setSelectedValue(value);
            onSelect?.(value);
        },
        [onSelect]
    );

    const getOptionLabel = useCallback((option: JobOfferOption | string) => {
        if (typeof option === 'string') return option;
        return `${option.jobNumber} - ${option.title}`;
    }, []);

    const isOptionEqualToValue = useCallback(
        (option: JobOfferOption, value: JobOfferOption) => option._id === value._id,
        []
    );

    // Reset selected job offer when scope changes to a different Pro.
    React.useEffect(() => {
        setSelectedValue(null);
        onSelect?.(null);
    }, [proId, onSelect]);

    return (
        <Autocomplete<JobOfferOption>
            options={options}
            loading={loading}
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            value={selectedValue}
            onChange={handleChange}
            onInputChange={handleInputChange}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            PopperComponent={PopperWithEscHint}
            noOptionsText={
                debouncedQuery.length === 0
                    ? 'Tapez pour rechercher...'
                    : 'Aucune offre trouvée'
            }
            loadingText="Chargement..."
            size={size}
            fullWidth
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={placeholder}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? (
                                    <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option._id}>
                    <Stack direction="row" gap={1.5} alignItems="center" sx={{ width: '100%' }}>
                        <Image
                            width={32}
                            height={32}
                            variant="circle"
                            src={option.pro?.brandLogo?.url || option.brand?.brandLogo?.url || '/default-logo.png'}
                            alt={option.title}
                        />
                        <Stack sx={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontWeight: 600, fontSize: '14px' }}>
                                {option.title}
                            </span>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <span
                                    style={{
                                        fontSize: '13px',
                                        color: '#666',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {option.jobNumber}
                                </span>
                                {option.pro?.commercialName && (
                                    <span
                                        style={{
                                            fontSize: '12px',
                                            color: '#999',
                                        }}
                                    >
                                        • {option.pro.commercialName}
                                    </span>
                                )}
                            </Box>
                            
                            <span
                                style={{
                                    fontSize: '12px',
                                    color: '#999',
                                }}
                            >
                                {option.city} ({option.postalCode})
                            </span>
                        </Stack>
                    </Stack>
                </Box>
            )}
        />
    );
};

export default AutocompleteJobOffer;
